/**
 * Parasite Rate URL State
 *
 * Serializes and restores the full shareable state of the Parasite Rate map to
 * and from URL query parameters. This lets a user copy the current URL and send
 * it to someone else so they open the map with the same filters, selected
 * point, warning (validation-failure) mode, and dataset version.
 *
 * The encoding is intentionally compact and human-inspectable: multi-value
 * filters are joined with commas, booleans are "1"/absent, and the selected
 * point is stored as a composite key of stable identifying fields (see
 * {@link buildPointKey}). Parsing is lenient about unknown/malformed values in
 * the sense that it ignores them, but it never silently substitutes a
 * different filter selection - an absent parameter simply means "use the
 * component default", which is the documented behaviour.
 */

import type { ParasiteRatePoint } from "@/services/parasiteRateService"
import type { AgeGroup } from "./FilterList"

// ---------------------------------------------------------------------------
// Query parameter names
// ---------------------------------------------------------------------------

/**
 * Centralised names of every query parameter this module reads and writes.
 * Kept short to keep shared URLs manageable.
 */
export const URL_PARAM = {
  /** Dataset version string (e.g. "v2.3"). */
  version: "v",
  /** Color-by column key (e.g. "computed_total_pf_positive_rate"). */
  colorColumn: "color",
  /** Size-scale identifier ("logarithmic" | "linear" | "sqrt"). */
  sizeScale: "size",
  /** Comma-separated selected species labels. */
  species: "species",
  /** Comma-separated selected method values. */
  methods: "methods",
  /** Comma-separated selected country ISO3 codes. */
  countries: "countries",
  /** Comma-separated selected source IDs. */
  sourceIds: "sources",
  /** Comma-separated selected MAP survey IDs. */
  mapSurveyIds: "surveys",
  /** "1" when the survey-only toggle is on; absent otherwise. */
  onlySurvey: "surveyOnly",
  /** Comma-separated selected age groups. */
  ageGroups: "ages",
  /** Date-range start as a year decimal (e.g. "2005.5"). */
  dateStart: "dateStart",
  /** Date-range end as a year decimal. */
  dateEnd: "dateEnd",
  /** "1" when validation-failure (warning) mode is active; absent otherwise. */
  warning: "warning",
  /** Composite key identifying the selected point. */
  point: "point",
  /** Map view longitude (center of the current bounding box). */
  longitude: "lng",
  /** Map view latitude (center of the current bounding box). */
  latitude: "lat",
  /** Map zoom level. */
  zoom: "zoom",
  /** Map pitch (tilt) in degrees. */
  pitch: "pitch",
  /** Map bearing (rotation) in degrees. */
  bearing: "bearing",
} as const

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/**
 * The camera / bounding-box portion of the map state. deck.gl describes the
 * visible area with a center (longitude/latitude), zoom, and optional
 * pitch/bearing rather than explicit corner coordinates; together these fully
 * determine the bounding box for a given viewport size.
 */
export interface MapViewUrlState {
  /** View center longitude. */
  longitude: number
  /** View center latitude. */
  latitude: number
  /** Zoom level. */
  zoom: number
  /** Tilt in degrees. */
  pitch: number
  /** Rotation in degrees. */
  bearing: number
}

/**
 * The complete set of shareable map state. Every field is optional: an absent
 * field means the URL did not specify it and the component should keep its own
 * default. This is what {@link parseUrlState} returns and what
 * {@link buildSearchParams} consumes.
 */
export interface ParasiteRateUrlState {
  /** Dataset version string, or undefined if not specified. */
  version?: string
  /** Color-by column key, or undefined. */
  colorColumn?: string
  /** Size-scale identifier, or undefined. */
  sizeScale?: string
  /** Selected species labels, or undefined. */
  selectedSpecies?: string[]
  /** Selected method values, or undefined. */
  selectedMethods?: string[]
  /** Selected country ISO3 codes, or undefined. */
  selectedCountries?: string[]
  /** Selected source IDs, or undefined. */
  selectedSourceIds?: string[]
  /** Selected MAP survey IDs, or undefined. */
  selectedMapSurveyIds?: string[]
  /** Survey-only toggle value, or undefined. */
  onlySurveyData?: boolean
  /** Selected age groups, or undefined. */
  selectedAgeGroups?: AgeGroup[]
  /** Date-range start as a year decimal, or undefined. */
  dateRangeStart?: number
  /** Date-range end as a year decimal, or undefined. */
  dateRangeEnd?: number
  /** Warning (validation-failure) mode active, or undefined. */
  errorModeActive?: boolean
  /** Composite key of the selected point, or undefined. */
  pointKey?: string
  /** The map view (bounding box) if fully specified in the URL, or undefined. */
  view?: MapViewUrlState
}

// ---------------------------------------------------------------------------
// Point identity
// ---------------------------------------------------------------------------

/** Separator used inside a composite point key. Chosen to avoid clashing with data values. */
const POINT_KEY_SEP = "~"

/**
 * Build a stable composite key that uniquely identifies a data point across
 * page loads. Points have no single guaranteed unique ID, so this combines a
 * set of fields that together locate one observation: source ID, coordinates,
 * survey start (year/month), and examined sample size.
 *
 * The same key is produced for the same underlying observation regardless of
 * object identity, which is what allows a shared URL to re-select the point
 * after data reloads.
 *
 * @param point - The parasite rate (or validation failure) point to identify.
 * @returns A separator-joined string key. Null fields become empty segments.
 */
export function buildPointKey(point: ParasiteRatePoint): string {
  // Coordinates are rounded to 5 decimal places (~1m precision) to avoid
  // spurious mismatches from floating-point round-tripping through the URL.
  const lat: string = point.latitude != null ? point.latitude.toFixed(5) : ""
  const lon: string = point.longitude != null ? point.longitude.toFixed(5) : ""

  const segments: string[] = [
    point.source_id1 ?? "",
    lat,
    lon,
    point.year_start != null ? String(point.year_start) : "",
    point.month_start != null ? String(point.month_start) : "",
    point.examined != null ? String(point.examined) : "",
  ]

  return segments.join(POINT_KEY_SEP)
}

/**
 * Find the point in a list whose composite key matches the given key.
 *
 * @param points - The candidate points to search (filtered or unfiltered).
 * @param pointKey - The composite key produced by {@link buildPointKey}.
 * @returns The matching point, or null if no point matches.
 */
export function findPointByKey<T extends ParasiteRatePoint>(
  points: readonly T[],
  pointKey: string,
): T | null {
  for (const point of points) {
    if (buildPointKey(point) === pointKey) {
      return point
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Parsing (URL -> state)
// ---------------------------------------------------------------------------

/** Separator used to join multi-value list parameters in the URL. */
const LIST_SEP = ","

/**
 * Serialize an array of values into a single comma-separated query value.
 *
 * Each value is individually URL-encoded before joining, so values that
 * themselves contain the separator — e.g. a method named
 * "Microscopy, confirmed by PCR" — survive the round-trip intact instead of
 * being split. {@link parseList} reverses this.
 *
 * @param values - The list of values to serialize.
 * @returns A comma-joined string of per-value-encoded segments.
 */
function serializeList(values: string[]): string {
  return values.map((v) => encodeURIComponent(v)).join(LIST_SEP)
}

/**
 * Split a comma-separated query value into a trimmed, non-empty string array,
 * decoding each segment produced by {@link serializeList}.
 *
 * @param raw - The raw parameter value, or null if the parameter is absent.
 * @returns An array of values, or undefined if the parameter was absent.
 */
function parseList(raw: string | null): string[] | undefined {
  if (raw == null) {
    return undefined
  }
  return raw
    .split(LIST_SEP)
    // Decode each segment to restore separators (and any other reserved
    // characters) that were escaped inside individual values.
    .map((v) => decodeURIComponent(v.trim()))
    .filter((v) => v.length > 0)
}

/**
 * Parse a query value as a finite number.
 *
 * @param raw - The raw parameter value, or null if the parameter is absent.
 * @returns The parsed number, or undefined if absent or not a finite number.
 */
function parseNumber(raw: string | null): number | undefined {
  if (raw == null) {
    return undefined
  }
  const parsed: number = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Read the full shareable map state out of a URLSearchParams instance.
 *
 * @param params - The URLSearchParams to read from (e.g. from useSearchParams).
 * @returns A {@link ParasiteRateUrlState} with only the fields present in the URL.
 */
export function parseUrlState(params: URLSearchParams): ParasiteRateUrlState {
  const version: string | null = params.get(URL_PARAM.version)
  const colorColumn: string | null = params.get(URL_PARAM.colorColumn)
  const sizeScale: string | null = params.get(URL_PARAM.sizeScale)
  const onlySurveyRaw: string | null = params.get(URL_PARAM.onlySurvey)
  const warningRaw: string | null = params.get(URL_PARAM.warning)
  const pointKey: string | null = params.get(URL_PARAM.point)

  // Parse the map view. Longitude, latitude, and zoom are required for a
  // usable view; pitch and bearing default to 0 (flat, north-up) when absent.
  const longitude: number | undefined = parseNumber(params.get(URL_PARAM.longitude))
  const latitude: number | undefined = parseNumber(params.get(URL_PARAM.latitude))
  const zoom: number | undefined = parseNumber(params.get(URL_PARAM.zoom))
  const view: MapViewUrlState | undefined =
    longitude != null && latitude != null && zoom != null
      ? {
          longitude,
          latitude,
          zoom,
          pitch: parseNumber(params.get(URL_PARAM.pitch)) ?? 0,
          bearing: parseNumber(params.get(URL_PARAM.bearing)) ?? 0,
        }
      : undefined

  return {
    version: version ?? undefined,
    colorColumn: colorColumn ?? undefined,
    sizeScale: sizeScale ?? undefined,
    selectedSpecies: parseList(params.get(URL_PARAM.species)),
    selectedMethods: parseList(params.get(URL_PARAM.methods)),
    selectedCountries: parseList(params.get(URL_PARAM.countries)),
    selectedSourceIds: parseList(params.get(URL_PARAM.sourceIds)),
    selectedMapSurveyIds: parseList(params.get(URL_PARAM.mapSurveyIds)),
    onlySurveyData: onlySurveyRaw != null ? onlySurveyRaw === "1" : undefined,
    selectedAgeGroups: parseList(params.get(URL_PARAM.ageGroups)) as AgeGroup[] | undefined,
    dateRangeStart: parseNumber(params.get(URL_PARAM.dateStart)),
    dateRangeEnd: parseNumber(params.get(URL_PARAM.dateEnd)),
    errorModeActive: warningRaw != null ? warningRaw === "1" : undefined,
    pointKey: pointKey ?? undefined,
    view,
  }
}

// ---------------------------------------------------------------------------
// Serialization (state -> URL)
// ---------------------------------------------------------------------------

/**
 * Input describing the current, fully-resolved map state to serialize. Unlike
 * {@link ParasiteRateUrlState} every field is required here because the caller
 * (the map component) always has a concrete value for each piece of state.
 */
export interface SerializableMapState {
  /** Dataset version string. */
  version: string
  /** Color-by column key. */
  colorColumn: string
  /** Size-scale identifier. */
  sizeScale: string
  /** Selected species labels. */
  selectedSpecies: string[]
  /** Selected method values. */
  selectedMethods: string[]
  /** Selected country ISO3 codes. */
  selectedCountries: string[]
  /** Selected source IDs. */
  selectedSourceIds: string[]
  /** Selected MAP survey IDs. */
  selectedMapSurveyIds: string[]
  /** Survey-only toggle value. */
  onlySurveyData: boolean
  /** Selected age groups. */
  selectedAgeGroups: AgeGroup[]
  /**
   * Effective date-range start (year decimal). Pass null when the user has not
   * narrowed the start, so it is omitted from the URL and the recipient's map
   * falls back to the dataset minimum.
   */
  dateRangeStart: number | null
  /** Effective date-range end (year decimal), or null when not narrowed. */
  dateRangeEnd: number | null
  /** Warning (validation-failure) mode active. */
  errorModeActive: boolean
  /** Composite key of the selected point, or null when nothing is selected. */
  selectedPointKey: string | null
  /**
   * The current map view (bounding box), or null before the map has reported
   * a view state. When null the view params are omitted from the URL.
   */
  view: MapViewUrlState | null
}

/**
 * Build a URLSearchParams that fully captures the given map state. Multi-value
 * filters are comma-joined; booleans are written only when true; the date range
 * and selected point are written only when present.
 *
 * @param state - The current, fully-resolved map state to serialize.
 * @returns A URLSearchParams ready to attach to the current location.
 */
export function buildSearchParams(state: SerializableMapState): URLSearchParams {
  const params = new URLSearchParams()

  if (state.version) {
    params.set(URL_PARAM.version, state.version)
  }
  params.set(URL_PARAM.colorColumn, state.colorColumn)
  params.set(URL_PARAM.sizeScale, state.sizeScale)

  if (state.selectedSpecies.length > 0) {
    params.set(URL_PARAM.species, serializeList(state.selectedSpecies))
  }
  if (state.selectedMethods.length > 0) {
    params.set(URL_PARAM.methods, serializeList(state.selectedMethods))
  }
  if (state.selectedCountries.length > 0) {
    params.set(URL_PARAM.countries, serializeList(state.selectedCountries))
  }
  if (state.selectedSourceIds.length > 0) {
    params.set(URL_PARAM.sourceIds, serializeList(state.selectedSourceIds))
  }
  if (state.selectedMapSurveyIds.length > 0) {
    params.set(URL_PARAM.mapSurveyIds, serializeList(state.selectedMapSurveyIds))
  }
  if (state.onlySurveyData) {
    params.set(URL_PARAM.onlySurvey, "1")
  }
  if (state.selectedAgeGroups.length > 0) {
    params.set(URL_PARAM.ageGroups, serializeList(state.selectedAgeGroups))
  }
  if (state.dateRangeStart != null) {
    // Two decimal places is enough to represent a month (1/12 ≈ 0.083).
    params.set(URL_PARAM.dateStart, state.dateRangeStart.toFixed(2))
  }
  if (state.dateRangeEnd != null) {
    params.set(URL_PARAM.dateEnd, state.dateRangeEnd.toFixed(2))
  }
  if (state.errorModeActive) {
    params.set(URL_PARAM.warning, "1")
  }
  if (state.selectedPointKey != null) {
    params.set(URL_PARAM.point, state.selectedPointKey)
  }
  if (state.view != null) {
    // Coordinates get more precision than zoom/pitch/bearing since small
    // longitude/latitude differences are visible; 5 dp is ~1m.
    params.set(URL_PARAM.longitude, state.view.longitude.toFixed(5))
    params.set(URL_PARAM.latitude, state.view.latitude.toFixed(5))
    params.set(URL_PARAM.zoom, state.view.zoom.toFixed(2))
    // Only include pitch/bearing when non-zero to keep the common flat,
    // north-up view's URL short. Absent values decode back to 0.
    if (state.view.pitch !== 0) {
      params.set(URL_PARAM.pitch, state.view.pitch.toFixed(2))
    }
    if (state.view.bearing !== 0) {
      params.set(URL_PARAM.bearing, state.view.bearing.toFixed(2))
    }
  }

  return params
}
